import { createSlice } from "@reduxjs/toolkit";
import {
  IAnalyticsOverview,
  IGetDashboardResponse,
  IIncidentMetrics,
  IIncidentsInBranch,
  IIncidentsInFraudNature,
  IIncidentsInFraudType,
  IIncidentsInMonth,
  IIncidentsInZone,
} from "../../../api/dashboard";
import { RootState } from "../../../app/store";
import { fetchDashboardMetricsThunk } from "./dashboardThunk";

export interface DashboardState {
  data?: IGetDashboardResponse;
}

const initialState: DashboardState = {
  data: undefined,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardMetrics: (state) => {
      state.data = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDashboardMetricsThunk.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

export const { clearDashboardMetrics } = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;

export const selectDashboardState = (state: RootState) => state.dashboardData;
export const selectDashboardRaw = (state: RootState) =>
  state.dashboardData.data;

const ZERO_METRICS: IIncidentMetrics = {
  total_cases_opened: 0,
  total_fraud_amount: 0,
};

const ZERO_ANALYTICS: IAnalyticsOverview = {
  cases_opened: 0,
  fraud_amount: 0,
  cases_declared_fraud: 0,
  cases_declared_no_fraud: 0,
  cases_in_progress: 0,
  cases_open: 0,
  cases_deactivated: 0,
  cases_closed: 0,
};

export const selectDashboardAnalyticsOverview = (
  state: RootState,
): IAnalyticsOverview =>
  state.dashboardData.data?.analytics_overview ?? ZERO_ANALYTICS;

export const selectDashboardIncidentsByZone = (
  state: RootState,
): IIncidentsInZone[] =>
  state.dashboardData.data?.incidents_by_zone?.zones ?? [];

export const selectDashboardIncidentsByBranch = (
  state: RootState,
): IIncidentsInBranch[] =>
  state.dashboardData.data?.incidents_by_branch?.branches ?? [];

export const selectDashboardIncidentsByFraudType = (
  state: RootState,
): IIncidentsInFraudType[] =>
  state.dashboardData.data?.incidents_by_fraud_type?.fraud_types ?? [];

export const selectDashboardIncidentsByFraudNature = (
  state: RootState,
): IIncidentsInFraudNature[] =>
  state.dashboardData.data?.incidents_by_nature_of_fraud?.nature_of_fraud ?? [];

export const selectDashboardIncidentsByMonth = (
  state: RootState,
): IIncidentsInMonth[] =>
  state.dashboardData.data?.incidents_by_month?.month ?? [];

export interface DashboardMetricPoint {
  label: string;
  metrics: IIncidentMetrics;
}

export const selectZoneMetricPoints = (
  state: RootState,
): DashboardMetricPoint[] =>
  selectDashboardIncidentsByZone(state).map((z) => ({
    label: z.zone,
    metrics: z.metrics ?? ZERO_METRICS,
  }));

export const selectBranchMetricPoints = (
  state: RootState,
): DashboardMetricPoint[] =>
  selectDashboardIncidentsByBranch(state).map((b) => ({
    label: b.branch,
    metrics: b.metrics ?? ZERO_METRICS,
  }));

export const selectFraudTypeMetricPoints = (
  state: RootState,
): DashboardMetricPoint[] =>
  selectDashboardIncidentsByFraudType(state).map((ft) => ({
    label: ft.fraud_type,
    metrics: ft.metrics ?? ZERO_METRICS,
  }));

export const selectFraudNatureMetricPoints = (
  state: RootState,
): DashboardMetricPoint[] =>
  selectDashboardIncidentsByFraudNature(state).map((n) => ({
    label: n.nature_of_fraud,
    metrics: n.metrics ?? ZERO_METRICS,
  }));

export interface DashboardMonthlyMetricPoint {
  label: string;
  month: string;
  year: string;
  metrics: IIncidentMetrics;
}

export const selectMonthlyMetricPoints = (
  state: RootState,
): DashboardMonthlyMetricPoint[] =>
  selectDashboardIncidentsByMonth(state)
    .slice()
    .sort((a, b) => {
      const ay = Number(a.year);
      const by = Number(b.year);
      const am = Number(a.month);
      const bm = Number(b.month);

      const aInvalid = Number.isNaN(ay) || Number.isNaN(am);
      const bInvalid = Number.isNaN(by) || Number.isNaN(bm);
      if (aInvalid && bInvalid) return 0;
      if (aInvalid) return 1;
      if (bInvalid) return -1;

      if (ay !== by) return ay - by;
      return am - bm;
    })
    .map((m) => ({
      label: `${m.month} ${m.year}`,
      month: m.month,
      year: m.year,
      metrics: m.metrics ?? ZERO_METRICS,
    }));
